import { useLocation, useParams } from "react-router-dom";
import { useMemo } from "react";
import { useGetOpportunity } from "@/services/opportunities/queries";

type BreadcrumbItem = {
  title: string;
  link?: string;
};

// This allows to add custom title as well
const routeMapping: Record<string, BreadcrumbItem[]> = {
  "/dashboard": [{ title: "Dashboard", link: "/dashboard" }],
  "/dashboard/overview": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Overview", link: "/dashboard/overview" },
  ],
  "/dashboard/organisations": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Organisations", link: "/dashboard/organisations" },
  ],
  "/dashboard/organisations/add": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Organisations", link: "/dashboard/organisations" },
    { title: "Add Organisation", link: "/dashboard/organisations/add" },
  ],
  "/dashboard/projects": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Projects", link: "/dashboard/projects" },
  ],
  "/dashboard/projects/add": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Projects", link: "/dashboard/projects" },
    { title: "Add Project", link: "/dashboard/projects/add" },
  ],
  "/dashboard/data": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Data", link: "/dashboard/data" },
  ],
  "/dashboard/data/:id/edit": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Data", link: "/dashboard/data" },
    { title: "Edit Data Set", link: "/dashboard/data/edit" },
  ],
  "/dashboard/data/update": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Data", link: "/dashboard/data" },
    { title: "Update Data Set", link: "/dashboard/data/update" },
  ],
  "/dashboard/map": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Map", link: "/dashboard/map" },
  ],
  "/dashboard/invitations": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Invitations", link: "/dashboard/invitations" },
  ],
  "/dashboard/inquiries": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Inquiries", link: "/dashboard/inquiries" },
  ],
  "/dashboard/inquiries/:id/reply": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Inquiries", link: "/dashboard/inquiries" },
    { title: "Reply to Inquiry", link: "/dashboard/inquiries/reply" },
  ],
  "/dashboard/projects/:id/edit": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Projects", link: "/dashboard/projects" },
    { title: "Edit Project", link: "/dashboard/projects/:id/edit" },
  ],
  "/dashboard/invitations/add": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Invitations", link: "/dashboard/invitations" },
    { title: "Add New User", link: "/dashboard/invitations/add" },
  ],
  "/dashboard/get-started": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Get Started", link: "/dashboard/get-started" },
  ],
  "/dashboard/help": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Help", link: "/dashboard/help" },
  ],
  "/dashboard/settings": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Settings", link: "/dashboard/settings" },
  ],
  "/dashboard/opportunities": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Opportunities", link: "/dashboard/opportunities" },
  ],
  "/dashboard/opportunities/:id": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Opportunities", link: "/dashboard/opportunities" },
    { title: "Opportunity Details", link: "/dashboard/opportunities/:id" },
  ],
  "/dashboard/opportunities/:id/edit": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Opportunities", link: "/dashboard/opportunities" },
    { title: "Edit", link: "/dashboard/opportunities/:id/edit" }, // Will be replaced with opportunity name
  ],
  "/dashboard/opportunities/:id/applications": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Opportunities", link: "/dashboard/opportunities" },
    { title: "Applications", link: "/dashboard/opportunities/:id/applications" }, // Will be replaced with opportunity name
  ],
  "/dashboard/opportunities/:id/applications/:applicationId": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Opportunities", link: "/dashboard/opportunities" },
    { title: "Applications", link: "/dashboard/opportunities/:id/applications" },
    {
      title: "Application Details",
      link: "/dashboard/opportunities/:id/applications/:applicationId",
    },
  ],
  "/dashboard/signify-interest/:id": [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Opportunities", link: "/dashboard/opportunities" },
    { title: "Signify Interest", link: "/dashboard/signify-interest/:id" },
  ],
  // Add more custom mappings as needed
};

export function useBreadcrumbs() {
  const location = useLocation();
  const params = useParams<{ id?: string }>();

  // Fetch opportunity data if we're on the edit or applications page
  const isEditOpportunityPage = location.pathname.match(
    /^\/dashboard\/opportunities\/[^/]+\/edit$/
  );
  const isApplicationsPage = location.pathname.match(
    /^\/dashboard\/opportunities\/[^/]+\/applications$/
  );
  const isApplicationDetailPage = location.pathname.match(
    /^\/dashboard\/opportunities\/[^/]+\/applications\/[^/]+$/
  );
  const { data: opportunityData } = useGetOpportunity(params.id || "", {
    enabled:
      (!!isEditOpportunityPage ||
        !!isApplicationsPage ||
        !!isApplicationDetailPage) &&
      !!params.id,
  });

  const breadcrumbs = useMemo(() => {
    // Check if we have a custom mapping for this exact path
    const matchedRoute = Object.keys(routeMapping).find((pattern) => {
      const regexPattern = new RegExp(
        "^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$"
      );
      return regexPattern.test(location.pathname);
    });

    if (matchedRoute) {
      // Process titles to remove hyphens and ensure proper capitalization
      let processedBreadcrumbs = routeMapping[matchedRoute].map((item) => ({
        ...item,
        title: item.title
          .replace(/-/g, " ")
          .split(" ")
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" "),
      }));

      // Special handling for edit opportunity page - replace "Edit" with "Edit [Opportunity Name]"
      if (
        matchedRoute === "/dashboard/opportunities/:id/edit" &&
        opportunityData?.title
      ) {
        const lastItem = processedBreadcrumbs[processedBreadcrumbs.length - 1];
        if (lastItem.title === "Edit") {
          lastItem.title = `Edit ${opportunityData.title}`;
        }
      }

      // Special handling for applications page - replace "Applications" with "[Opportunity Name] Applications"
      if (
        (matchedRoute === "/dashboard/opportunities/:id/applications" ||
          matchedRoute === "/dashboard/opportunities/:id/applications/:applicationId") &&
        opportunityData?.title
      ) {
        const applicationsItem = processedBreadcrumbs.find(
          (item) => item.title === "Applications"
        );
        if (applicationsItem) {
          applicationsItem.title = `${opportunityData.title} Applications`;
        }
      }

      return processedBreadcrumbs.map((item) => ({
        ...item,
        link: item.link?.includes(":") ? undefined : item.link,
      }));
    }
    // If no exact match, fall back to generating breadcrumbs from the path stop at two segments
    const segments = location.pathname.split("/").filter(Boolean);
    return segments.map((segment, index) => {
      const path = `/${segments.slice(0, index + 1).join("/")}`;
      // Replace hyphens with spaces and capitalize each word
      const formattedTitle = segment
        .replace(/-/g, " ")
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      return {
        title: formattedTitle,
        link: path,
      };
    });
  }, [location.pathname, opportunityData?.title]);

  return breadcrumbs;
}
